import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  FullScreenLoading,
  TrackAssessmentResult,
  AcademicEnrollmentType,
} from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { LAYOUT_SCROLL_ID } from '@backrs_ui/grow-ui/src/components/PageShell/pageShell.layout';
import { filterEditProfileProfileOptions } from '@backrs_ui/grow-ui/src/pages/Profile/utils/options.helpers';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  useFetchUserProfileInfoQuery,
  useFetchProfileValueOptionsQuery,
} from '@backrs_ui/grow-ui/src/redux/profile/profile';
import { USER_PROFILE_FIELDS } from '@backrs_ui/grow-ui/src/redux/profile/profile.constants';
import {
  useGetMostRecentTrackAssessmentResultQuery,
  useGetAllowReassessQuery,
  useGetTrackAssessmentHistoryQuery,
  useGetAcademicGradesQuery,
} from '@backrs_ui/grow-ui/src/redux/trackAssessment/trackAssessment.api';

import { Box, SxProps, Theme } from '@mui/material';

import { ChecklistDataOverrides } from './assessMyTrack.types';
import { hasChartableAssessment, isUnsupportedEnrollmentType } from './assessMyTrack.utils';
import { useAgentContext } from './AssessMyTrackPage.agentContext';
import { useAssessmentBreakdown } from './hooks/useAssessmentBreakdown';
import { useTrackAssessment } from './hooks/useTrackAssessment';
import { AssessmentBreakdown } from './molecules/AssessmentBreakdown';
import { AssessmentChecklistAcademicType } from './molecules/AssessmentChecklistAcademicType';
import { AssessmentChecklistReview } from './molecules/AssessmentChecklistReview';
import { AssessmentEmptyState } from './molecules/AssessmentEmptyState';
import { CareerTrackTimelineChart } from './molecules/CareerTrackTimelineChart';
import { ChecklistEditModal } from './molecules/ChecklistEditModal';
import { DimensionEditModal } from './molecules/DimensionEditModal';

const ASSESS_MY_TRACK_STRINGS = appStrings.modules.assessMyTrack;

const TENURE_MAP: Record<string, 'High School' | 'Post High School'> = {
  high_school: 'High School',
  post_secondary: 'Post High School',
};

const shouldHideContent = (
  academicEnrollmentType: AcademicEnrollmentType | undefined,
  checklistOverrides: ChecklistDataOverrides | undefined,
  assessmentData: TrackAssessmentResult | null,
) => {
  const hasValidResult = assessmentData?.academicGrade && assessmentData?.dimensionScores?.length;

  return (!academicEnrollmentType && !checklistOverrides) || !hasValidResult;
};

type AssessMyTrackPageProps = Readonly<{
  checklistOverrides?: ChecklistDataOverrides;
  results?: TrackAssessmentResult;
  isShared?: boolean;
}>;

export const AssessMyTrackPage = ({
  checklistOverrides,
  results,
  isShared = false,
}: AssessMyTrackPageProps) => {
  const navigate = useNavigate();
  const { loadingMostRecentUserResponseSet } = useAppSelector((state) => state.userResponseSet);
  const [isChartLoading, setIsChartLoading] = useState(false);
  const [assessFailed, setAssessFailed] = useState(false);
  const [viewOverride, setViewOverride] = useState<'review' | null>(null);
  const {
    data: profileData,
    isLoading: isLoadingUserProfile,
    refetch,
  } = useFetchUserProfileInfoQuery(USER_PROFILE_FIELDS, { skip: isShared });

  const { data: unfilteredProfileOptions } = useFetchProfileValueOptionsQuery(undefined, {
    skip: isShared,
  });
  const profileOptions = filterEditProfileProfileOptions(unfilteredProfileOptions);

  const {
    data: mostRecentResult,
    isLoading: isLoadingMostRecent,
    refetch: refetchMostRecent,
  } = useGetMostRecentTrackAssessmentResultQuery(undefined, {
    skip: !!results, // if we are viewing old results, do not fetch most recent
  });

  const {
    data: allowReassessData,
    refetch: refetchAllowReassess,
    isFetching: isFetchingAllowReassess,
  } = useGetAllowReassessQuery(undefined, { skip: isShared });

  const {
    data: assessmentHistory,
    isLoading: isLoadingHistory,
    isError: isErrorHistory,
  } = useGetTrackAssessmentHistoryQuery(undefined);
  const {
    data: academicGrades,
    isLoading: isLoadingGrades,
    isError: isErrorGrades,
  } = useGetAcademicGradesQuery(undefined);

  const {
    handleAssess,
    isAssessing: isAssessingCareerTrack,
    trackAssessmentResult,
  } = useTrackAssessment();

  const assessmentBreakdown = useAssessmentBreakdown();

  const { getBreakdownForAssessment, makeCriteriaDescriptionGetter } = assessmentBreakdown;

  const latestBreakdown = useMemo(
    () => getBreakdownForAssessment(mostRecentResult),
    [getBreakdownForAssessment, mostRecentResult],
  );

  // Criteria are the thresholds the result was scored against, so they key off
  // the grade it was scored at — not the grade it's now shown at. The name has
  // to come from the same grade, or the panel labels one and lists the other's.
  const mostRecentGradeId = mostRecentResult?.academic_grade_id;
  const mostRecentGradeName = academicGrades?.find((grade) => grade.id === mostRecentGradeId)?.name;
  const mostRecentCareerTrackId = mostRecentResult?.careerTrack?.id;
  const mostRecentTenure = mostRecentResult?.enrollment_type_level
    ? TENURE_MAP[mostRecentResult.enrollment_type_level]
    : undefined;

  const getCriteriaDescriptionFull = useMemo(
    () =>
      makeCriteriaDescriptionGetter(
        mostRecentGradeId,
        false,
        mostRecentCareerTrackId,
        mostRecentTenure,
      ),
    [makeCriteriaDescriptionGetter, mostRecentGradeId, mostRecentCareerTrackId, mostRecentTenure],
  );

  const criteriaDescriptions = assessmentBreakdown.editingDimension
    ? {
        advanced: getCriteriaDescriptionFull(
          assessmentBreakdown.editingDimension.categorySlug,
          assessmentBreakdown.editingDimension.dimensionName,
          'Advanced',
        ),
        minimum: getCriteriaDescriptionFull(
          assessmentBreakdown.editingDimension.categorySlug,
          assessmentBreakdown.editingDimension.dimensionName,
          'Minimum',
        ),
      }
    : undefined;

  const allowReassess = !isFetchingAllowReassess && (allowReassessData?.allowReassess ?? false);

  const academicEnrollmentType = profileData?.profile.academicEnrollmentTypes?.level;

  useEffect(() => {
    const backrsLayout = document.getElementById(LAYOUT_SCROLL_ID);

    backrsLayout?.scrollTo({ top: 0 });
  }, []);

  useEffect(() => {
    if (profileData && !isAssessingCareerTrack && !isShared) {
      refetchAllowReassess();
    }
  }, [profileData, refetchAllowReassess, mostRecentResult, isAssessingCareerTrack, isShared]);

  // use passed-in results, then the current results, then the most recent results
  const assessmentData = results || trackAssessmentResult || mostRecentResult || null;

  useAgentContext(assessmentData);

  const handleAssessMyTrack = async () => {
    setIsChartLoading(true);
    setAssessFailed(false);

    const assessmentResult = await handleAssess();

    if (!assessmentResult) {
      setIsChartLoading(false);
      setAssessFailed(true);

      return;
    }

    setViewOverride(null);
    setIsChartLoading(false);
    refetchMostRecent();
    refetchAllowReassess();
  };

  // redirect if we're not viewing results, and the user is not a high school student
  useEffect(() => {
    if (
      !checklistOverrides &&
      isUnsupportedEnrollmentType(profileData?.profile.academicEnrollmentTypes)
    ) {
      navigate(APP_ROUTE.HOME);
    }
  }, [checklistOverrides, profileData?.profile.academicEnrollmentTypes, navigate]);

  if (
    isLoadingUserProfile ||
    isLoadingMostRecent ||
    loadingMostRecentUserResponseSet ||
    (isLoadingHistory && !isErrorHistory) ||
    (isLoadingGrades && !isErrorGrades)
  ) {
    return <FullScreenLoading />;
  }

  const hideContent = shouldHideContent(academicEnrollmentType, checklistOverrides, assessmentData);

  type ViewState = 'enrollment' | 'empty' | 'review' | 'chart';

  // History drives the chart; skip when viewing passed-in results (history is the viewer's).
  const noChartableData =
    !results && !isErrorHistory && !isErrorGrades && !hasChartableAssessment(assessmentHistory);

  const hasResults = !hideContent && !noChartableData;

  const getViewState = (): ViewState => {
    if (isChartLoading && hasResults) return 'chart';
    if (!hasResults && !academicEnrollmentType && !checklistOverrides) return 'enrollment';
    if (!hasResults) return 'empty';
    if (viewOverride === 'review') return 'review';

    return 'chart';
  };

  const viewState = getViewState();

  return (
    <Box sx={containerSx}>
      <Box sx={subContainerSx}>
        {viewState === 'enrollment' && <AssessmentChecklistAcademicType />}
        {viewState === 'empty' && (
          <AssessmentEmptyState
            profileData={profileData}
            checklistDataOverrides={checklistOverrides}
            handleAssessMyTrack={handleAssessMyTrack}
            isLoading={isAssessingCareerTrack || isChartLoading}
            allowReassess={allowReassess}
            hasError={assessFailed}
          />
        )}
        {viewState === 'review' && (
          <AssessmentChecklistReview
            profileData={profileData}
            checklistDataOverrides={checklistOverrides}
            handleAssessMyTrack={handleAssessMyTrack}
            isLoading={isAssessingCareerTrack}
            allowReassess={allowReassess}
            onBack={() => setViewOverride(null)}
            hasError={assessFailed}
          />
        )}
        {viewState === 'chart' && (
          <Box sx={contentContainerSx}>
            <CareerTrackTimelineChart
              isLoading={isChartLoading}
              assessmentBreakdown={assessmentBreakdown}
              trackAssessmentResult={assessmentData ?? undefined}
              onEditChecklist={() => setViewOverride('review')}
              includeBlueprintText
              showLastAssessed
            />
          </Box>
        )}
        {latestBreakdown &&
          viewState === 'chart' &&
          !isAssessingCareerTrack &&
          !isChartLoading &&
          mostRecentGradeName && (
            <AssessmentBreakdown
              breakdown={latestBreakdown}
              gradeName={mostRecentGradeName}
              tenure={mostRecentTenure}
              academicGradeId={mostRecentGradeId}
              onDimensionClick={assessmentBreakdown.handleDimensionClick}
            />
          )}

        <DimensionEditModal
          key={assessmentBreakdown.editingDimension?.scoreId}
          open={!!assessmentBreakdown.editingDimension}
          onClose={assessmentBreakdown.clearEditingDimension}
          dimension={assessmentBreakdown.editingDimension}
          onSubmit={assessmentBreakdown.handleDimensionSubmit}
          criteriaDescriptions={criteriaDescriptions}
          isSubmitting={assessmentBreakdown.isSubmitting}
          hasSubmitError={!!assessmentBreakdown.submitError}
        />

        {assessmentBreakdown.editingProfileField && profileOptions && (
          <ChecklistEditModal
            open={!!assessmentBreakdown.editingProfileField}
            onClose={() => assessmentBreakdown.setEditingProfileField(null)}
            onSubmit={() => {
              assessmentBreakdown.setEditingProfileField(null);
              refetch();
              handleAssessMyTrack();
              setTimeout(() => {
                document.getElementById(LAYOUT_SCROLL_ID)?.scrollTo({ top: 0, behavior: 'smooth' });
              }, 300);
            }}
            profileOptions={profileOptions}
            fieldKey={assessmentBreakdown.editingProfileField}
            description={
              ASSESS_MY_TRACK_STRINGS.assessmentChecklist
                .thisInformationWillBeUpdatedInYourProfileAndReassessed
            }
          />
        )}
      </Box>
    </Box>
  );
};

const containerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  margin: '0 auto',
  alignItems: 'center',
  containerType: 'inline-size',
  overflow: 'auto',
};

const subContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-2xl'],
  width: '100%',
  pb: backrs.spacing['spacing-4xl'],
});

const contentContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  width: '100%',
  gap: backrs.spacing['spacing-2xl'],
  '@container (min-width: 875px)': {
    flexDirection: 'row',
    minHeight: '600px',
  },
});
